import { IBasketItem } from '@/context/BasketStore'
import { IProduct } from '@/context/ProductDataStore'
import { KibuCareProductPricingEntry, StripeCheckoutItem } from '@/services/api'
import { buildKibuCareCheckoutItems } from './kibuCare'

export const mergeBasketProduct = (current: IProduct, updated?: IProduct): IProduct => {
  if (!updated || updated._id !== current._id) return current
  return {
    ...updated,
    components: current.components ?? updated.components,
    stockLimits: updated.stockLimits ?? current.stockLimits,
    availableOptions: updated.availableOptions ?? current.availableOptions,
  }
}

const buildProductCheckoutItem = (product: IProduct, quantity: number = 1): StripeCheckoutItem => {
  if (product.components?.length) {
    const materialComponents = product.components
      .filter(component => Boolean(component.chosenMaterial?.$oid))

    const checkoutItem: StripeCheckoutItem = {
      productItemId: product._id,
      quantity,
    }

    if (materialComponents.length) {
      checkoutItem.components = materialComponents.map(component => ({
        partId: component.componentVariantId,
        side: component.position || undefined,
        chosenMaterial: component.chosenMaterial
      }))
    }

    return checkoutItem
  }

  return {
    productItemId: product._id,
    quantity,
  }
}

export const buildCheckoutItemsFromBasket = ({
  basketItems,
  selectedKibuCarePlanId,
  kibuCareProductPricing,
}: {
  basketItems: IBasketItem[]
  selectedKibuCarePlanId?: string | null
  kibuCareProductPricing?: KibuCareProductPricingEntry[]
}): {
  items: StripeCheckoutItem[]
  kibuCareItems: StripeCheckoutItem[]
} => {
  const checkoutItems: StripeCheckoutItem[] = []

  basketItems.forEach((basketItem) => {
    const quantity = basketItem.quantity ?? 1
    checkoutItems.push(buildProductCheckoutItem(basketItem.colourWay, quantity))

    basketItem.extras.forEach((item: IProduct) => {
      // Addons apply per parent unit, so their quantity tracks the parent's quantity.
      const checkoutItem = buildProductCheckoutItem(item, quantity)
      checkoutItem.parentProductItemId = String(basketItem.colourWay._id)

      const engravingInput = basketItem.extrasData.find(data => data.itemId === item._id)
      if (item.componentModifications?.engravingText && engravingInput) {
        checkoutItem.componentModifications = {
          engravingText: engravingInput.inputValue
        }
      }

      checkoutItems.push(checkoutItem)
    })
  })

  const kibuCareItems = buildKibuCareCheckoutItems({
    selectedPlanId: selectedKibuCarePlanId,
    basketItems,
    productPricing: kibuCareProductPricing || [],
  })

  checkoutItems.push(...kibuCareItems)

  return {
    items: checkoutItems,
    kibuCareItems,
  }
}
